import { FlaskIcon, GlobeSimpleIcon, TelegramLogoIcon } from '@phosphor-icons/react';
import { cn } from '@smena/ui';
import type { ChannelTransport, ClientChannel } from './api';

// Маркер канала: заливной квадрат цвета транспорта с белым глифом внутри.
//
// Почему заливка, а не тонированная иконка: у MAX и ВКонтакте фирменных глифов в наборе нет,
// и буква «M» тонированным текстом читается как текст, а не как знак. Квадрат даёт одну форму
// всем пяти транспортам и держит контраст на любой подложке - белое на цвете канала.
//
// Один и тот же элемент в списке, ленте, композере и карточке: глаз учится один раз.

const BG: Record<ChannelTransport, string> = {
  telegram: 'bg-tg',
  max: 'bg-max',
  vk: 'bg-vk',
  web: 'bg-web',
  sim: 'bg-sim',
};

const SIZE = {
  sm: 'size-[13px] rounded-[3px] text-[7px]',
  md: 'size-4 rounded-[4px] text-[9px]',
  lg: 'size-6 rounded-md text-[12px]',
} as const;

const GLYPH_SIZE = { sm: 'size-[9px]', md: 'size-[11px]', lg: 'size-[15px]' } as const;

export type ChannelMarkSize = keyof typeof SIZE;

/**
 * `live` - показывать текущую доступность: у лежащего канала маркер сереет. В ленте `live`
 * не ставим: сообщение пришло через этот канал независимо от того, работает ли он сейчас.
 */
export function ChannelMark({
  channel,
  size = 'md',
  live = false,
  className,
}: {
  channel: Pick<ClientChannel, 'transport' | 'status' | 'label'>;
  size?: ChannelMarkSize;
  live?: boolean;
  className?: string;
}) {
  const down = live && channel.status === 'down';
  const glyph = GLYPH_SIZE[size];
  return (
    <span
      aria-hidden
      title={channel.label}
      className={cn(
        'inline-grid shrink-0 place-items-center font-extrabold tracking-tight text-white',
        SIZE[size],
        down ? 'bg-line-control' : BG[channel.transport],
        className,
      )}
    >
      {channel.transport === 'telegram' ? (
        <TelegramLogoIcon weight="fill" className={glyph} />
      ) : channel.transport === 'web' ? (
        <GlobeSimpleIcon weight="bold" className={glyph} />
      ) : channel.transport === 'sim' ? (
        <FlaskIcon weight="fill" className={glyph} />
      ) : channel.transport === 'max' ? (
        'M'
      ) : (
        'VK'
      )}
    </span>
  );
}

/** Маркер плюс подпись «Telegram · ваш номер»: транспорт насыщенным, режим приглушённым. */
export function ChannelLabel({
  channel,
  size = 'md',
  live = false,
  className,
}: {
  channel: Pick<ClientChannel, 'transport' | 'status' | 'label' | 'title' | 'sub'>;
  size?: ChannelMarkSize;
  live?: boolean;
  className?: string;
}) {
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap', className)}>
      <ChannelMark channel={channel} size={size} live={live} />
      <span className="font-semibold">{channel.title}</span>
      <span className="min-w-0 truncate text-muted">· {channel.sub}</span>
    </span>
  );
}

/** «в MAX · бот Smena» / «во ВКонтакте · сообщество» - предлог по транспорту. */
export function inChannel(channel: Pick<ClientChannel, 'transport' | 'label'>): string {
  return `${channel.transport === 'vk' ? 'во' : 'в'} ${channel.label}`;
}

/** «в MAX», «во ВКонтакте» - только транспорт: для кнопок и подсказок. */
export function intoTransport(channel: Pick<ClientChannel, 'transport' | 'title'>): string {
  const word = channel.transport === 'web' ? 'веб-чат' : channel.transport === 'sim' ? 'симулятор' : channel.title;
  return `${channel.transport === 'vk' ? 'во' : 'в'} ${word}`;
}

/** Живые каналы выше, внутри - по свежести входящих; лежащие внизу, dormant последними. */
export function byChannelOrder(a: ClientChannel, b: ClientChannel): number {
  const rank = (c: ClientChannel) => (c.dormant ? 2 : c.status === 'down' ? 1 : 0);
  const diff = rank(a) - rank(b);
  if (diff !== 0) return diff;
  return (b.lastInboundAt ?? '') < (a.lastInboundAt ?? '') ? -1 : 1;
}
